import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { dashboardApi } from '../services/api'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, ReferenceLine,
} from 'recharts'
import { TrendingUp, Award, Target, Zap, Download, Users, RefreshCw, CalendarDays, BarChart2 } from 'lucide-react'

const MESI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#14b8a6']

export default function Report() {
  const anno = new Date().getFullYear()
  const [tab, setTab] = useState<'generale' | 'tecnici'>('generale')
  const [filtroAnno, setFiltroAnno] = useState(anno)
  const [filtroMese, setFiltroMese] = useState<number | ''>('')
  const [cercaTecnico, setCercaTecnico] = useState('')

  const { data: andamento } = useQuery({
    queryKey: ['andamento-mensile'],
    queryFn: () => dashboardApi.andamentoMensile(anno).then(r => r.data),
  })
  const { data: perTipologia } = useQuery({
    queryKey: ['per-tipologia'],
    queryFn: () => dashboardApi.perTipologia().then(r => r.data),
  })
  const { data: perStato } = useQuery({
    queryKey: ['per-stato'],
    queryFn: () => dashboardApi.perStato().then(r => r.data),
  })
  const { data: perTecnico = [], isLoading: loadingTecnici, isError: errorTecnici, refetch: refetchTecnici } = useQuery({
    queryKey: ['per-tecnico', filtroAnno, filtroMese],
    queryFn: () => dashboardApi.perTecnico({ anno: filtroAnno, mese: filtroMese || undefined }).then(r => r.data),
    enabled: tab === 'tecnici',
  })

  const chartData = andamento?.dati?.map((d: {
    mese: number; work_orders: number; completati: number; fatturato: number
    giorni_attivi?: number; tecnici_attivi?: number; wo_per_giorno?: number; wo_per_tecnico?: number
  }) => ({
    ...d, mese: MESI[d.mese - 1],
    tasso: d.work_orders > 0 ? Math.round((d.completati / d.work_orders) * 100) : 0,
    wo_per_giorno: d.wo_per_giorno ?? 0,
    wo_per_tecnico: d.wo_per_tecnico ?? 0,
    tecnici_attivi: d.tecnici_attivi ?? 0,
    giorni_attivi: d.giorni_attivi ?? 0,
  })) || []
  const tipologiaData = (perTipologia || []).map((d: { tipologia: string; count: number }) => ({ name: d.tipologia.replace(/_/g, ' '), value: d.count }))
  const statoData = (perStato || []).map((d: { stato: string; count: number }) => ({ name: d.stato.replace(/_/g, ' '), value: d.count }))
  const totaleWO = chartData.reduce((s: number, d: { work_orders: number }) => s + d.work_orders, 0)
  const totaleCompletati = chartData.reduce((s: number, d: { completati: number }) => s + d.completati, 0)
  const tassoCompletamento = totaleWO > 0 ? Math.round((totaleCompletati / totaleWO) * 100) : 0
  const totaleFatturato = chartData.reduce((s: number, d: { fatturato: number }) => s + d.fatturato, 0)

  const tecniciFiltrati = (perTecnico as any[]).filter(t =>
    !cercaTecnico || `${t.nome} ${t.cognome}`.toLowerCase().includes(cercaTecnico.toLowerCase())
  )
  const barData = tecniciFiltrati.map((t: any) => ({ name: `${t.nome} ${t.cognome}`.split(' ').slice(0, 2).join(' '), completati: t.wo_completati, assegnati: t.wo_assegnati }))

  const handleExport = async () => {
    try {
      const res = await dashboardApi.exportPerTecnicoXlsx({ anno: filtroAnno, mese: filtroMese || undefined })
      const url = URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url; a.download = `performance_tecnici_${filtroAnno}.xlsx`; a.click()
      URL.revokeObjectURL(url)
    } catch { /* silenzioso */ }
  }

  const TAB_STYLE = (active: boolean) => ({
    padding: '7px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
    background: active ? '#1E40AF' : 'transparent',
    color: active ? '#fff' : '#6b7280',
    transition: 'all 0.15s',
  })

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="page-title">Report & Business Intelligence</h1>
          <p className="page-subtitle">Analisi operativa — {anno}</p>
        </div>
        <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 10, padding: 4 }}>
          <button style={TAB_STYLE(tab === 'generale')} onClick={() => setTab('generale')}>
            Generale
          </button>
          <button style={TAB_STYLE(tab === 'tecnici')} onClick={() => setTab('tecnici')}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Users size={13} /> Performance Tecnici
            </span>
          </button>
        </div>
      </div>

      {/* ── TAB GENERALE ── */}
      {tab === 'generale' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'WO Totali', val: totaleWO, icon: Target, cls: 'text-blue-600', sub: `Anno ${anno}` },
              { label: 'Completati', val: totaleCompletati, icon: Award, cls: 'text-green-600', sub: `${tassoCompletamento}% tasso` },
              { label: 'Tasso FCR', val: `${tassoCompletamento}%`, icon: Zap, cls: 'text-amber-600', sub: 'First Call Resolution' },
              { label: 'Fatturato', val: `€${(totaleFatturato / 1000).toFixed(1)}k`, icon: TrendingUp, cls: 'text-indigo-600', sub: `Anno ${anno}` },
            ].map((kpi) => (
              <div key={kpi.label} className="card p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{kpi.label}</p>
                  <p className={`text-3xl font-bold mt-0.5 ${kpi.cls}`}>{kpi.val}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{kpi.sub}</p>
                </div>
                <kpi.icon size={24} className={`${kpi.cls} opacity-50`} />
              </div>
            ))}
          </div>

          {/* Produttività per mese */}
          {chartData.some((d: any) => d.wo_per_giorno > 0 || d.wo_per_tecnico > 0) && (
            <>
              <div className="card p-5">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <BarChart2 size={15} style={{ color: '#1E40AF' }} />
                  <h3 className="font-semibold">Produttività — WO per Giorno Lavorativo e per Tecnico</h3>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData} margin={{ top: 4, right: 20, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="mese" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={32} />
                    <Tooltip
                      contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                      formatter={(v: number, name: string) => [v.toFixed(2), name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="wo_per_giorno" name="WO/Giorno Lav." stroke="#F97316" strokeWidth={2} dot={{ r: 3, fill: '#F97316', strokeWidth: 0 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="wo_per_tecnico" name="WO/Tecnico" stroke="#1E40AF" strokeWidth={2} dot={{ r: 3, fill: '#1E40AF', strokeWidth: 0 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="card overflow-hidden">
                <div className="p-4 border-b border-slate-100" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CalendarDays size={15} style={{ color: '#6b7280' }} />
                  <h3 className="font-semibold">Dettaglio Produttività per Mese — {anno}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                        {['Mese', 'Completati', 'Giorni Attivi', 'Tecnici Att.', 'WO / Giorno', 'WO / Tecnico'].map(h => (
                          <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {chartData.filter((d: any) => d.completati > 0 || d.giorni_attivi > 0).map((d: any) => {
                        const isCurrentMonth = d.mese === MESI[new Date().getMonth()]
                        return (
                          <tr key={d.mese}
                            style={{ borderBottom: '1px solid #f1f5f9', background: isCurrentMonth ? '#eff6ff' : undefined }}
                            onMouseEnter={e => { if (!isCurrentMonth) e.currentTarget.style.background = '#f8fafc' }}
                            onMouseLeave={e => { if (!isCurrentMonth) e.currentTarget.style.background = 'transparent' }}>
                            <td style={{ padding: '9px 14px', fontWeight: isCurrentMonth ? 700 : 500, color: isCurrentMonth ? '#1E40AF' : '#374151' }}>
                              {d.mese}{isCurrentMonth && <span style={{ marginLeft: 6, fontSize: 10, background: '#dbeafe', color: '#1E40AF', borderRadius: 99, padding: '1px 6px', fontWeight: 600 }}>oggi</span>}
                            </td>
                            <td style={{ padding: '9px 14px', fontWeight: 600, color: '#1E40AF' }}>{d.completati}</td>
                            <td style={{ padding: '9px 14px', color: '#6b7280' }}>{d.giorni_attivi || '—'}</td>
                            <td style={{ padding: '9px 14px', color: '#8b5cf6', fontWeight: 600 }}>{d.tecnici_attivi || '—'}</td>
                            <td style={{ padding: '9px 14px' }}>
                              <span style={{
                                fontFamily: 'monospace', fontWeight: 700, fontSize: 13,
                                color: d.wo_per_giorno >= 2 ? '#16a34a' : d.wo_per_giorno >= 1 ? '#d97706' : '#6b7280',
                              }}>
                                {d.wo_per_giorno > 0 ? d.wo_per_giorno.toFixed(2) : '—'}
                              </span>
                            </td>
                            <td style={{ padding: '9px 14px' }}>
                              <span style={{
                                fontFamily: 'monospace', fontWeight: 700, fontSize: 13,
                                color: d.wo_per_tecnico >= 3 ? '#16a34a' : d.wo_per_tecnico >= 1 ? '#d97706' : '#6b7280',
                              }}>
                                {d.wo_per_tecnico > 0 ? d.wo_per_tecnico.toFixed(2) : '—'}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <div className="card p-5">
            <h3 className="font-semibold mb-4">Andamento Work Orders e Tasso Completamento — {anno}</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="mese" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="work_orders" name="WO Creati" fill="#3b82f6" radius={[3,3,0,0]} />
                <Bar yAxisId="left" dataKey="completati" name="Completati" fill="#22c55e" radius={[3,3,0,0]} />
                <Line yAxisId="right" type="monotone" dataKey="tasso" name="Tasso %" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {tipologiaData.length > 0 && (
              <div className="card p-5">
                <h3 className="font-semibold mb-4">Interventi per Tipologia</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={tipologiaData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={3} strokeWidth={0}>
                      {tipologiaData.map((_: unknown, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            {statoData.length > 0 && (
              <div className="card p-5">
                <h3 className="font-semibold mb-4">Distribuzione per Stato</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={statoData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={3} strokeWidth={0}>
                      {statoData.map((_: unknown, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── TAB PERFORMANCE TECNICI ── */}
      {tab === 'tecnici' && (
        <>
          {/* Filtri */}
          <div className="card p-4 flex flex-wrap gap-3 items-end">
            <div>
              <label className="label">Anno</label>
              <select className="input w-28" value={filtroAnno} onChange={e => setFiltroAnno(Number(e.target.value))}>
                {[anno, anno - 1, anno - 2].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Mese</label>
              <select className="input w-32" value={filtroMese} onChange={e => setFiltroMese(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Tutti</option>
                {MESI.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Cerca tecnico</label>
              <input className="input w-44" placeholder="Nome..." value={cercaTecnico} onChange={e => setCercaTecnico(e.target.value)} />
            </div>
            <div className="flex-1" />
            <button
              onClick={() => refetchTecnici()}
              disabled={loadingTecnici}
              className="btn-secondary flex items-center gap-2"
              title="Aggiorna dati"
            >
              <RefreshCw size={14} style={{ animation: loadingTecnici ? 'spin 1s linear infinite' : 'none' }} />
              {loadingTecnici ? 'Caricamento...' : 'Aggiorna'}
            </button>
            <button onClick={handleExport} className="btn-secondary flex items-center gap-2">
              <Download size={14} /> Esporta XLSX
            </button>
          </div>
          {errorTecnici && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 16px', color: '#dc2626', fontSize: 13 }}>
              ⚠ Errore caricamento performance tecnici. <button onClick={() => refetchTecnici()} style={{ textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 13 }}>Riprova</button>
            </div>
          )}

          {/* Bar chart orizzontale */}
          {barData.length > 0 && (
            <div className="card p-5">
              <h3 className="font-semibold mb-4">WO Completati per Tecnico</h3>
              <ResponsiveContainer width="100%" height={Math.max(200, barData.length * 44)}>
                <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={110} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="assegnati" name="Assegnati" fill="#bfdbfe" radius={[0,3,3,0]} />
                  <Bar dataKey="completati" name="Completati" fill="#1E40AF" radius={[0,3,3,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Tabella classifica */}
          <div className="card overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="font-semibold">Classifica Tecnici — {filtroAnno}{filtroMese ? ` · ${MESI[filtroMese - 1]}` : ''}</h3>
            </div>
            {loadingTecnici ? (
              <div className="p-8 text-center text-sm text-slate-400">Caricamento...</div>
            ) : tecniciFiltrati.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">Nessun dato disponibile</div>
            ) : (
              <div className="overflow-x-auto">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                      {['#', 'Tecnico', 'Assegnati', 'Completati', 'Tasso', 'Media Giorni', 'SLA ✓', 'SLA ✗', 'Km'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tecniciFiltrati.map((t: any, i: number) => (
                      <tr key={t.tecnico_id} style={{ borderBottom: '1px solid #f1f5f9' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ width: 22, height: 22, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: i < 3 ? ['#FEF08A','#E2E8F0','#FED7AA'][i] : '#f1f5f9', color: '#374151' }}>{i + 1}</span>
                        </td>
                        <td style={{ padding: '10px 14px', fontWeight: 600 }}>{t.nome} {t.cognome}</td>
                        <td style={{ padding: '10px 14px', color: '#6b7280' }}>{t.wo_assegnati}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1E40AF' }}>{t.wo_completati}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ background: t.tasso_completamento >= 80 ? '#dcfce7' : t.tasso_completamento >= 60 ? '#fef9c3' : '#fee2e2', color: t.tasso_completamento >= 80 ? '#166534' : t.tasso_completamento >= 60 ? '#854d0e' : '#991b1b', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700 }}>
                            {t.tasso_completamento}%
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px', color: '#6b7280' }}>{t.media_giorni_chiusura}g</td>
                        <td style={{ padding: '10px 14px', color: '#16a34a', fontWeight: 600 }}>{t.sla_rispettati}</td>
                        <td style={{ padding: '10px 14px', color: t.sla_violati > 0 ? '#dc2626' : '#9ca3af', fontWeight: t.sla_violati > 0 ? 700 : 400 }}>{t.sla_violati}</td>
                        <td style={{ padding: '10px 14px', color: '#6b7280' }}>{t.km_percorsi.toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
